import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Pagination,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { format } from "date-fns";

export default function UserDataTable({ data, loading = false, onDeleteUser }) {
  const [page, setPage] = React.useState(1);
  const [rowsPerPage] = React.useState(10);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const formatDate = (timestamp) => {
    return format(new Date(timestamp * 1000), "dd MMM yyyy hh:mm a");
  };

  const getSourceChipColor = (source) => {
    switch (source) {
      case "Live Cam Login":
        return "#f44336"; // Red color matching the image
      default:
        return "#24b8e7";
    }
  };

  const handleDeleteUser = (userIndex, userEmail) => {
    // Calculate the actual data index from the paginated index
    const actualIndex = startIndex + userIndex;
    const userData = data[actualIndex];

    // Store user data and show confirmation dialog
    setUserToDelete({ userData, actualIndex });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (onDeleteUser && userToDelete) {
      onDeleteUser(userToDelete.userData, userToDelete.actualIndex);
    }
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = data.slice(startIndex, endIndex);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#0d0e13",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* User Cards */}
      <Box sx={{ flex: 1, mb: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress sx={{ color: "#24b8e7" }} />
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              color: "#9e9e9e",
            }}
          >
            <Typography variant="h6">No user data available</Typography>
          </Box>
        ) : (
          paginatedData.map((user, index) => (
            <Card
              key={index}
              sx={{
                backgroundColor: "#1a1b23",
                border: "1px solid #333",
                mb: 2,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  {/* User Name and Source */}
                  <Grid item xs={12} md={3}>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          mb: 1,
                        }}
                      >
                        {user.name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label={user.captureSource}
                          size="small"
                          sx={{
                            backgroundColor: getSourceChipColor(
                              user.captureSource
                            ),
                            color: "white",
                            fontWeight: "bold",
                            "& .MuiChip-deleteIcon": {
                              color: "white",
                              "&:hover": {
                                color: "#ffcccb",
                              },
                            },
                          }}
                        />
                        <DeleteIcon
                          onClick={() =>
                            handleDeleteUser(index, user.emailAddress)
                          }
                          sx={{
                            ml: 1,
                            cursor: "pointer",
                            color: "white",
                            "&:hover": {
                              color: "#ffcccb",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  {/* User Details */}
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: "#9e9e9e", mb: 0.5 }}
                          >
                            Email:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {user.emailAddress}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: "#9e9e9e", mb: 0.5 }}
                          >
                            Age:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {user.age || "N.A."}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: "#9e9e9e", mb: 0.5 }}
                          >
                            Phone:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {user.phone || "N.A."}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: "#9e9e9e", mb: 0.5 }}
                          >
                            Date:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "white" }}>
                            {formatDate(user.saveDate)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Location Details and Consents */}
                  <Grid item xs={12} md={3}>
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "#9e9e9e", mb: 0.5 }}
                        >
                          Address:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "white" }}>
                          {user.address || "N.A."}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "#9e9e9e", mb: 0.5 }}
                        >
                          Location:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "white" }}>
                          {user.location || "N.A."}
                        </Typography>
                      </Box>

                      {/* Consent Fields */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "#9e9e9e", mb: 1, fontSize: "0.85rem" }}
                        >
                          Consent Status:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {user.ageConsent && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#9e9e9e",
                                  fontSize: "0.75rem",
                                  minWidth: "60px",
                                }}
                              >
                                Age:
                              </Typography>
                              <Chip
                                label={user.ageConsent}
                                size="small"
                                sx={{
                                  backgroundColor: "#24b8e7",
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                  height: "18px",
                                }}
                              />
                            </Box>
                          )}

                          {user.nameConsent && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#9e9e9e",
                                  fontSize: "0.75rem",
                                  minWidth: "60px",
                                }}
                              >
                                Name:
                              </Typography>
                              <Chip
                                label={user.nameConsent}
                                size="small"
                                sx={{
                                  backgroundColor: "#24b8e7",
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                  height: "18px",
                                }}
                              />
                            </Box>
                          )}

                          {user.emailConsent && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#9e9e9e",
                                  fontSize: "0.75rem",
                                  minWidth: "60px",
                                }}
                              >
                                Email:
                              </Typography>
                              <Chip
                                label={user.emailConsent}
                                size="small"
                                sx={{
                                  backgroundColor: "#24b8e7",
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                  height: "18px",
                                }}
                              />
                            </Box>
                          )}

                          {user.termsConsent && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#9e9e9e",
                                  fontSize: "0.75rem",
                                  minWidth: "60px",
                                }}
                              >
                                Terms:
                              </Typography>
                              <Chip
                                label={user.termsConsent}
                                size="small"
                                sx={{
                                  backgroundColor: "#24b8e7",
                                  color: "white",
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                  height: "18px",
                                }}
                              />
                            </Box>
                          )}

                          {!user.ageConsent &&
                            !user.nameConsent &&
                            !user.emailConsent &&
                            !user.termsConsent && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#666",
                                  fontSize: "0.75rem",
                                  fontStyle: "italic",
                                }}
                              >
                                N.A.
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          px: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
          Rows per page: {rowsPerPage}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
            {startIndex + 1}-{Math.min(endIndex, data.length)} of {data.length}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "white",
                borderColor: "#333",
                "&:hover": {
                  backgroundColor: "rgba(36, 184, 231, 0.1)",
                },
                "&.Mui-selected": {
                  backgroundColor: "#24b8e7",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#1a94c7",
                  },
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{ color: "#fff", backgroundColor: "#1a1b23" }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1a1b23" }}>
          <DialogContentText
            id="delete-dialog-description"
            sx={{ color: "#9e9e9e" }}
          >
            Are you sure you want to delete this contact?
            {userToDelete && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#2a2b33",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  <strong>Email:</strong> {userToDelete.userData?.emailAddress}
                </Typography>
                {userToDelete.userData?.name && (
                  <Typography variant="body2" sx={{ color: "#fff" }}>
                    <strong>Name:</strong> {userToDelete.userData.name}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  <strong>Source:</strong>{" "}
                  {userToDelete.userData?.captureSource}
                </Typography>
              </Box>
            )}
            <Typography sx={{ mt: 2, color: "#f44336" }}>
              This action cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1a1b23", p: 2 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              color: "#9e9e9e",
              "&:hover": {
                backgroundColor: "#2a2b33",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: "#f44336",
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
